import React from "react";
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const formatRevenue = (v) => {
  if (v >= 10000000) return `₹${(v / 10000000)?.toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000)?.toFixed(1)}L`;
  return `₹${v?.toLocaleString('en-IN')}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border p-3 text-sm"
      style={{
        background: "var(--color-popover)",
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-lg)",
        fontFamily: "var(--font-caption)",
      }}
    >
      <p className="font-semibold mb-2" style={{ color: "var(--color-foreground)" }}>{label}</p>
      {payload?.map((entry) => entry?.value != null && (
        <div key={entry?.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry?.color || entry?.stroke }} />
          <span style={{ color: "var(--color-text-secondary)" }}>{entry?.name}:</span>
          <span className="font-data font-semibold" style={{ color: "var(--color-foreground)" }}>
            {formatRevenue(entry?.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function ForecastTrendChart({ data }) {
  const trend = data || [];
  // Re-audit fix: a month with zero actual revenue is "no data yet", not a
  // perfect 100% forecast hit — only months with real actuals count.
  const withActual = trend.filter((item) => item?.actual != null && item?.actual > 0 && item?.forecast != null);
  const accuracy = withActual.length
    ? (withActual.reduce((acc, item) => {
        const base = Math.max(item.actual || 1, 1);
        const diff = Math.abs((item.forecast || 0) - (item.actual || 0));
        return acc + Math.max(0, 100 - (diff / base) * 100);
      }, 0) / withActual.length)
    : null;
  const avgVariance = withActual.length
    ? withActual.reduce((acc, item) => acc + Math.abs((item.forecast || 0) - (item.actual || 0)), 0) / withActual.length
    : null;
  const future = trend.filter((item) => item?.actual == null && item?.forecast != null);
  const forecastTarget = future.reduce((acc, item) => acc + (item.forecast || 0), 0);
  const todayMonth = trend.find((item) => item?.actual == null)?.month;

  return (
    <div
      className="card-base flex flex-col gap-4 min-w-0"
      style={{ background: "var(--color-card)", borderRadius: "var(--radius-md)" }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg" style={{ color: "var(--color-foreground)" }}>
            Sales Forecast Accuracy
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            Actual vs forecast revenue — open deals by expected close month.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded" style={{ background: "var(--color-primary)", display: "inline-block" }} />
            <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>Actual</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded border-dashed border" style={{ borderColor: "var(--color-accent)", display: "inline-block" }} />
            <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>Forecast</span>
          </div>
        </div>
      </div>
      <div className="w-full" style={{ height: "220px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="confidenceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fontFamily: "var(--font-caption)", fill: "var(--color-text-secondary)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatRevenue}
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "var(--color-text-secondary)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip content={<CustomTooltip />} />
            {todayMonth && (
              <ReferenceLine x={todayMonth} stroke="var(--color-border)" strokeDasharray="4 4" label={{ value: "Today", fontSize: 10, fill: "var(--color-text-secondary)", fontFamily: "var(--font-caption)" }} />
            )}
            <Area
              type="monotone"
              dataKey="upper"
              stroke="none"
              fill="url(#confidenceGrad)"
              name="Upper Bound"
            />
            <Area
              type="monotone"
              dataKey="lower"
              stroke="none"
              fill="var(--color-card)"
              name="Lower Bound"
            />
            <Line
              type="monotone"
              dataKey="forecast"
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              name="Forecast"
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="var(--color-primary)"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "var(--color-primary)", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              name="Actual"
              connectNulls={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t" style={{ borderColor: "var(--color-border)" }}>
        {[
          { label: "Forecast Accuracy", value: accuracy == null ? "-" : `${accuracy.toFixed(1)}%`, color: "var(--color-success)" },
          { label: "Avg Variance", value: avgVariance == null ? "-" : formatRevenue(avgVariance), color: "var(--color-foreground)" },
          { label: "Future Target", value: forecastTarget ? formatRevenue(forecastTarget) : "-", color: "var(--color-primary)" },
          { label: "Confidence Level", value: "-", color: "var(--color-foreground)" },
        ]?.map((item) => (
          <div key={item?.label} className="flex flex-col gap-0.5">
            <span className="font-data text-base font-bold" style={{ color: item?.color }}>{item?.value}</span>
            <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>{item?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}